import { useCallback, useEffect, useState } from "react";
import { FunnelChart } from "./FunnelChart";
import type { StatsResponse } from "../types";

export function Dashboard() {
  const [data, setData] = useState<StatsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [domain, setDomain] = useState("");
  const [days, setDays] = useState(7);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError("");
    const params = new URLSearchParams({ days: String(days) });
    if (domain) params.set("domain", domain);

    try {
      const res = await fetch(`/api/stats?${params}`);
      if (!res.ok) {
        setError("Failed to load stats");
        return;
      }
      setData(await res.json<StatsResponse>());
    } catch (err) {
      console.error("Stats fetch failed:", err);
      setError("Connection failed");
    } finally {
      setLoading(false);
    }
  }, [domain, days]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return (
    <div className="mx-auto min-h-screen max-w-5xl px-4 py-6 sm:px-6">
      <header className="mb-8">
        <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-500">Hub &amp; Spoke</p>
        <h1 className="text-2xl font-bold text-white">Analytics Hub</h1>
      </header>

      <div className="mb-6 flex flex-wrap gap-3">
        <select
          value={domain}
          onChange={e => setDomain(e.target.value)}
          className="rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-white focus:border-emerald-500 focus:outline-none"
        >
          <option value="">All Domains</option>
          {(data?.domains ?? []).map(d => (
            <option key={d.domain_origin} value={d.domain_origin}>
              {d.domain_origin} ({d.sessions})
            </option>
          ))}
        </select>

        <select
          value={days}
          onChange={e => setDays(Number(e.target.value))}
          className="rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-white focus:border-emerald-500 focus:outline-none"
        >
          <option value={1}>Last 24 hours</option>
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
        </select>

        <button
          onClick={fetchStats}
          disabled={loading}
          className="rounded-lg bg-slate-800 px-4 py-2 text-sm font-medium text-slate-300 hover:bg-slate-700 disabled:opacity-50"
        >
          {loading ? "Loading…" : "Refresh"}
        </button>
      </div>

      {error && (
        <div className="mb-6 rounded-lg border border-red-800 bg-red-950/50 px-4 py-3 text-sm text-red-300">
          {error}
        </div>
      )}

      {data && (
        <>
          <div className="mb-8 grid gap-3 sm:grid-cols-3">
            <SummaryCard label="Page Views" value={data.summary.page_views.toLocaleString()} />
            <SummaryCard
              label="Phone Clicks"
              value={data.summary.phone_clicks.toLocaleString()}
              accent="emerald"
            />
            <SummaryCard
              label="Conversion Rate"
              value={`${data.summary.conversion_rate}%`}
              accent="amber"
              sub="page_view → phone_click"
            />
          </div>

          <FunnelChart funnel={data.funnel} />

          <div className="mt-8 grid gap-4 sm:grid-cols-2">
            <BreakdownCard title="Phone Clicks by Device" rows={data.phone_clicks_by_device} labelKey="device_profile" />
            <BreakdownCard title="Traffic by Agent" rows={data.traffic_by_agent} labelKey="user_agent_simplified" />
          </div>

          {data.domains.length > 0 && (
            <div className="mt-8 rounded-xl border border-slate-800 bg-slate-900 p-4">
              <h3 className="mb-3 text-xs font-bold uppercase tracking-widest text-slate-400">Active Domains</h3>
              <div className="space-y-2">
                {data.domains.map(d => (
                  <button
                    key={d.domain_origin}
                    onClick={() => setDomain(d.domain_origin)}
                    className={`flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm transition-colors ${
                      domain === d.domain_origin
                        ? "bg-emerald-950 text-emerald-300 ring-1 ring-emerald-700"
                        : "bg-slate-800/50 text-slate-300 hover:bg-slate-800"
                    }`}
                  >
                    <span className="font-mono">{d.domain_origin}</span>
                    <span className="text-xs text-slate-500">{d.sessions} sessions</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function SummaryCard({
  label,
  value,
  accent,
  sub,
}: {
  label: string;
  value: string;
  accent?: "emerald" | "amber";
  sub?: string;
}) {
  const accentClass =
    accent === "emerald" ? "text-emerald-400" : accent === "amber" ? "text-amber-400" : "text-white";

  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900 p-4">
      <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{label}</p>
      <p className={`mt-1 font-mono text-2xl font-bold ${accentClass}`}>{value}</p>
      {sub && <p className="mt-1 text-[10px] text-slate-500">{sub}</p>}
    </div>
  );
}

function BreakdownCard({
  title,
  rows,
  labelKey,
}: {
  title: string;
  rows: Array<Record<string, string | number>>;
  labelKey: string;
}) {
  const total = rows.reduce((sum, r) => sum + (Number(r.events ?? r.sessions) || 0), 0);

  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900 p-4">
      <h3 className="mb-3 text-xs font-bold uppercase tracking-widest text-slate-400">{title}</h3>
      {rows.length === 0 ? (
        <p className="text-xs text-slate-500">No data yet</p>
      ) : (
        <div className="space-y-2">
          {rows.map(row => {
            const count = Number(row.events ?? row.sessions) || 0;
            const pct = total > 0 ? Math.round((count / total) * 100) : 0;
            const label = String(row[labelKey] ?? "unknown");
            return (
              <div key={label} className="flex items-center justify-between text-sm">
                <span className="capitalize text-slate-300">{label}</span>
                <span className="font-mono text-xs text-slate-500">
                  {count} ({pct}%)
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
